import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit2, Trash2, Save, X, Upload, ExternalLink, ImageOff, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (img) => {
  if (!img) return '';
  if (img.startsWith('http://') || img.startsWith('https://')) return img;
  return BACKEND_URL + img;
};

const availableThicknesses = ['6 mm', '1,2 cm', '2 cm', '3 cm'];

export default function AdminMaterials() {
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filterCategory, setFilterCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isNewCategoryDialogOpen, setIsNewCategoryDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [coverage, setCoverage] = useState([]);
  const [coverageError, setCoverageError] = useState(false);
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [suggestions, setSuggestions] = useState({}); // {materialId: {nearest, nearest_source, nearest_url, best_score}}
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    price_per_m2: '',
    image_url: '',
    supplier_url: '',
    description: '',
    thickness_options: []
  });

  useEffect(() => {
    fetchMaterials();
    fetchCategories();
    fetchCoverage();
    fetchSuggestions();
  }, []);

  const fetchCoverage = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/materials/scrape-report`);
      setCoverage(res.data.coverage || []);
      setCoverageError(false);
    } catch (e) {
      setCoverageError(true);
    }
  };

  const fetchSuggestions = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/materials/unmatched`);
      const map = {};
      for (const m of (res.data.materials || [])) {
        if (m.suggestion) map[m.id] = m.suggestion;
      }
      setSuggestions(map);
    } catch (e) {
      // optional feature; silent fail
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/categories`);
      setCategories(res.data.filter(c => c.is_active).sort((a, b) => (a.order || 0) - (b.order || 0)));
    } catch (e) {
      console.error('fetchCategories', e);
    }
  };

  const fetchMaterials = async () => {
    try {
      const response = await axios.get(`${API_URL}/materials/all`);
      setMaterials(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Nom de catégorie requis');
      return;
    }
    try {
      await axios.post(`${API_URL}/admin/categories`, {
        name: newCategoryName.trim(),
        order: categories.length + 1,
        is_active: true,
      });
      toast.success('Catégorie créée');
      await fetchCategories();
      setFormData({ ...formData, category: newCategoryName.trim() });
      setNewCategoryName('');
      setIsNewCategoryDialogOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur création catégorie');
    }
  };

  const openDialog = (material = null) => {
    if (material) {
      setEditingMaterial(material);
      setFormData({
        name: material.name,
        category: material.category,
        price_per_m2: material.price_per_m2 || '',
        image_url: material.image_url || '',
        supplier_url: material.supplier_url || '',
        description: material.description || '',
        thickness_options: material.thickness_options || []
      });
    } else {
      setEditingMaterial(null);
      setFormData({
        name: '',
        category: categories[0]?.name || '',
        price_per_m2: '',
        image_url: '',
        supplier_url: '',
        description: '',
        thickness_options: []
      });
    }
    setIsDialogOpen(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editingMaterial) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(
        `${API_URL}/admin/materials/${editingMaterial.id}/upload-image`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setFormData((p) => ({ ...p, image_url: res.data.image_url }));
      // Refresh the materials list so the table shows the new image
      fetchMaterials();
      toast.success('Image téléchargée');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'upload");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleClearImage = () => {
    setFormData((p) => ({ ...p, image_url: '' }));
  };

  const addThicknessOption = () => {
    setFormData({
      ...formData,
      thickness_options: [
        ...formData.thickness_options,
        { thickness: '2 cm', price_per_m2: 0, price_per_ml: 0, available: true }
      ]
    });
  };

  const removeThicknessOption = (index) => {
    const newOptions = [...formData.thickness_options];
    newOptions.splice(index, 1);
    setFormData({ ...formData, thickness_options: newOptions });
  };

  const updateThicknessOption = (index, field, value) => {
    const newOptions = [...formData.thickness_options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    setFormData({ ...formData, thickness_options: newOptions });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...formData,
        price_per_m2: parseFloat(formData.price_per_m2) || 0,
        thickness_options: formData.thickness_options.map(opt => ({
          thickness: opt.thickness,
          price_per_m2: parseFloat(opt.price_per_m2) || 0,
          price_per_ml: parseFloat(opt.price_per_ml) || 0,
          available: opt.available
        }))
      };

      if (editingMaterial) {
        await axios.put(`${API_URL}/admin/materials/${editingMaterial.id}`, dataToSend);
        toast.success('Matière mise à jour');
      } else {
        await axios.post(`${API_URL}/admin/materials`, dataToSend);
        toast.success('Matière créée');
      }
      setIsDialogOpen(false);
      fetchMaterials();
      fetchCoverage();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette matière ?')) return;
    try {
      await axios.delete(`${API_URL}/admin/materials/${id}`);
      toast.success('Matière supprimée');
      fetchMaterials();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleActive = async (material) => {
    try {
      await axios.put(`${API_URL}/admin/materials/${material.id}`, {
        is_active: !material.is_active
      });
      toast.success(material.is_active ? 'Matière désactivée' : 'Matière activée');
      fetchMaterials();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-materials">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Gestion des matières</h1>
          <p className="text-white/50 mt-1">
            {filterCategory === 'all'
              ? `${materials.length} matière(s)`
              : `${materials.filter(m => m.category === filterCategory).length} matière(s) dans ${filterCategory}`}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-white/60 text-sm cursor-pointer" data-testid="only-missing-toggle">
            <Checkbox
              checked={onlyMissing}
              onCheckedChange={(c) => setOnlyMissing(!!c)}
              className="border-white/20 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
            />
            <span>Sans image / URL</span>
          </label>
          {/* FILTRE PAR CATÉGORIE */}
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white rounded-none w-[200px]" data-testid="filter-category-select">
              <SelectValue placeholder="Toutes catégories" />
            </SelectTrigger>
            <SelectContent className="bg-[#1C1C1C] border-white/10">
              <SelectItem value="all" className="text-white">Toutes catégories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat.id} value={cat.name} className="text-white">{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button onClick={() => openDialog()} className="btn-primary flex items-center gap-2" data-testid="add-material-btn">
            <Plus className="w-4 h-4" />
            Ajouter
          </button>
        </div>
      </div>

      {/* COVERAGE PANEL — taux d'images & supplier_url par marque */}
      {coverage.length > 0 && !coverageError && (
        <div className="bg-[#0A0A0A] border border-white/10 p-4" data-testid="coverage-panel">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-white text-sm font-semibold">Couverture images & liens fournisseurs</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {coverage.map((c) => {
              const pctImg = c.total ? Math.round((c.with_image / c.total) * 100) : 0;
              const pctUrl = c.total ? Math.round((c.with_url / c.total) * 100) : 0;
              const isComplete = pctImg >= 95 && pctUrl >= 95;
              const isPartial = pctImg > 0 || pctUrl > 0;
              return (
                <button
                  key={c.category}
                  type="button"
                  onClick={() => { setFilterCategory(c.category); setOnlyMissing(false); }}
                  className={`text-left p-2.5 border transition-colors ${
                    isComplete ? 'border-green-500/30 hover:border-green-500/50' :
                    isPartial ? 'border-yellow-500/25 hover:border-yellow-500/50' :
                    'border-red-500/30 hover:border-red-500/50'
                  } bg-[#1C1C1C]`}
                  data-testid={`coverage-${c.category.toLowerCase()}`}
                >
                  <div className="text-white text-sm font-medium">{c.category}</div>
                  <div className="text-white/40 text-[11px] mt-0.5">{c.total} matières</div>
                  <div className="flex gap-1.5 mt-1.5 text-[11px]">
                    <span className={pctImg >= 95 ? 'text-green-400' : pctImg > 0 ? 'text-yellow-400' : 'text-red-400'}>
                      🖼 {pctImg}%
                    </span>
                    <span className={pctUrl >= 95 ? 'text-green-400' : pctUrl > 0 ? 'text-yellow-400' : 'text-red-400'}>
                      🔗 {pctUrl}%
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left text-white/50 text-xs uppercase tracking-wider py-4 px-3 w-[68px]">Img</th>
                <th className="text-left text-white/50 text-xs uppercase tracking-wider py-4 px-4">Nom</th>
                <th className="text-left text-white/50 text-xs uppercase tracking-wider py-4 px-4">Catégorie</th>
                <th className="text-left text-white/50 text-xs uppercase tracking-wider py-4 px-4">Épaisseurs</th>
                <th className="text-center text-white/50 text-xs uppercase tracking-wider py-4 px-3 w-[60px]">Lien</th>
                <th className="text-left text-white/50 text-xs uppercase tracking-wider py-4 px-4">Statut</th>
                <th className="text-right text-white/50 text-xs uppercase tracking-wider py-4 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {materials
                .filter(m => filterCategory === 'all' || m.category === filterCategory)
                .filter(m => !onlyMissing || !m.image_url || !m.supplier_url)
                .map((material, index) => (
                <motion.tr
                  key={material.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: Math.min(index * 0.01, 0.3) }}
                  className="border-b border-white/5 hover:bg-white/5"
                >
                  <td className="py-2 px-3">
                    <div className="w-12 h-12 bg-[#0A0A0A] border border-white/10 overflow-hidden flex items-center justify-center">
                      {material.image_url ? (
                        <img
                          src={resolveImg(material.image_url)}
                          alt={material.name}
                          loading="lazy"
                          className="w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      ) : (
                        <ImageOff className="w-4 h-4 text-white/20" />
                      )}
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div>
                      <p className="text-white font-medium">{material.name}</p>
                      {material.description && (
                        <p className="text-white/40 text-xs truncate max-w-xs">{material.description}</p>
                      )}
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <span className="text-[#D4AF37] text-sm">{material.category}</span>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex flex-wrap gap-1">
                      {material.thickness_options?.filter(t => t.available).map((opt, optIdx) => (
                        <span
                          key={`${material.id}-${opt.thickness}-${optIdx}`}
                          className="px-2 py-0.5 bg-white/10 text-white/70 text-xs"
                        >
                          {opt.thickness} - {opt.price_per_m2}€
                        </span>
                      ))}
                      {(!material.thickness_options || material.thickness_options.length === 0) && (
                        <span className="text-white/40 text-xs">Non configuré</span>
                      )}
                    </div>
                  </td>
                  <td className="py-4 px-3 text-center">
                    {material.supplier_url ? (
                      <a
                        href={material.supplier_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#D4AF37] hover:text-[#D4AF37]/80 inline-flex"
                        title={material.supplier_url}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    ) : (
                      <span className="text-white/20 text-xs">—</span>
                    )}
                  </td>
                  <td className="py-4 px-4">
                    <button
                      onClick={() => toggleActive(material)}
                      className={`px-3 py-1 text-xs uppercase tracking-wider ${
                        material.is_active 
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                          : 'bg-red-500/20 text-red-400 border border-red-500/30'
                      }`}
                    >
                      {material.is_active ? 'Actif' : 'Inactif'}
                    </button>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => openDialog(material)} className="p-2 text-white/40 hover:text-white" data-testid={`edit-material-${material.id}`}>
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(material.id)} className="p-2 text-white/40 hover:text-red-400" data-testid={`delete-material-${material.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-[#1C1C1C] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingMaterial ? 'Modifier la matière' : 'Nouvelle matière'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-white/60 text-sm mb-2 block">Nom *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  className="bg-[#0A0A0A] border-white/10 text-white rounded-none"
                  data-testid="material-name-input"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-white/60 text-sm">Catégorie *</label>
                  <button
                    type="button"
                    onClick={() => setIsNewCategoryDialogOpen(true)}
                    className="text-[#D4AF37] hover:text-[#D4AF37]/80 text-xs flex items-center gap-1"
                    data-testid="add-new-category-btn"
                  >
                    <Plus className="w-3 h-3" /> Nouvelle catégorie
                  </button>
                </div>
                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                  <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white rounded-none">
                    <SelectValue placeholder="Sélectionner une catégorie" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1C1C1C] border-white/10">
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.name} className="text-white hover:bg-white/10">{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid sm:grid-cols-[120px_1fr] gap-4 items-start">
              <div className="space-y-2">
                <label className="text-white/60 text-sm block">Aperçu</label>
                <div className="w-[120px] h-[120px] bg-[#0A0A0A] border border-white/10 overflow-hidden flex items-center justify-center" data-testid="material-image-preview">
                  {formData.image_url ? (
                    <img
                      src={resolveImg(formData.image_url)}
                      alt="aperçu"
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <ImageOff className="w-8 h-8 text-white/20" />
                  )}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleImageUpload}
                  className="hidden"
                  data-testid="material-image-file-input"
                />
                {editingMaterial ? (
                  <div className="flex gap-1.5">
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/25 disabled:opacity-50"
                      data-testid="upload-image-btn"
                    >
                      <Upload className="w-3 h-3" />
                      {uploading ? '...' : 'Upload'}
                    </button>
                    {formData.image_url && (
                      <button
                        type="button"
                        onClick={handleClearImage}
                        className="px-2 py-1.5 text-xs text-red-400 border border-red-400/30 hover:bg-red-400/10"
                        title="Effacer (n'enregistre qu'au Save)"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ) : (
                  <p className="text-[10px] text-white/40">Enregistrer d'abord la matière pour pouvoir uploader.</p>
                )}
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-white/60 text-sm mb-2 block">URL Image (manuelle, ou auto-renseignée par upload)</label>
                  <Input
                    value={formData.image_url}
                    onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                    className="bg-[#0A0A0A] border-white/10 text-white rounded-none"
                    placeholder="https://… ou /api/static/materials/…"
                    data-testid="material-image-url-input"
                  />
                </div>
                <div>
                  <label className="text-white/60 text-sm mb-2 block flex items-center gap-2">
                    URL Fournisseur
                    {formData.supplier_url && (
                      <a
                        href={formData.supplier_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#D4AF37] hover:underline inline-flex items-center gap-1 text-xs"
                      >
                        Ouvrir <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </label>
                  <Input
                    value={formData.supplier_url}
                    onChange={(e) => setFormData({...formData, supplier_url: e.target.value})}
                    className="bg-[#0A0A0A] border-white/10 text-white rounded-none"
                    placeholder="https://www.laminam.com/fra/fr/produits/…"
                    data-testid="material-supplier-url-input"
                  />
                  {/* Suggestion from last scraper run */}
                  {editingMaterial && suggestions[editingMaterial.id] && (
                    <div className="mt-2 p-2.5 bg-[#D4AF37]/8 border border-[#D4AF37]/25 text-xs flex items-start gap-2" data-testid="material-suggestion">
                      <AlertCircle className="w-3.5 h-3.5 text-[#D4AF37] mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-white/70">
                          Plus proche trouvé sur <span className="text-[#D4AF37]">{suggestions[editingMaterial.id].nearest_source}</span> :
                          {' '}<span className="text-white">{suggestions[editingMaterial.id].nearest}</span>
                          {' '}<span className="text-white/40">({Math.round(suggestions[editingMaterial.id].best_score * 100)}%)</span>
                        </div>
                        <div className="mt-1.5 flex gap-2 flex-wrap">
                          <a
                            href={suggestions[editingMaterial.id].nearest_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#D4AF37] hover:underline text-[11px] inline-flex items-center gap-1"
                          >
                            Vérifier <ExternalLink className="w-3 h-3" />
                          </a>
                          <button
                            type="button"
                            onClick={() => setFormData((p) => ({ ...p, supplier_url: suggestions[editingMaterial.id].nearest_url }))}
                            className="text-[10px] uppercase tracking-wider text-white/80 hover:text-[#D4AF37] border border-white/20 px-2 py-0.5"
                            data-testid="apply-suggestion-btn"
                          >
                            Utiliser ce lien
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className="text-white/60 text-sm mb-2 block">Description</label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="bg-[#0A0A0A] border-white/10 text-white rounded-none resize-none"
                rows={2}
              />
            </div>

            {/* Thickness Options */}
            <div className="border-t border-white/10 pt-4">
              <div className="flex items-center justify-between mb-4">
                <label className="text-white font-semibold">Épaisseurs disponibles</label>
                <button 
                  type="button" 
                  onClick={addThicknessOption}
                  className="btn-secondary text-xs py-1 px-3 flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" />
                  Ajouter épaisseur
                </button>
              </div>

              {formData.thickness_options.length === 0 ? (
                <p className="text-white/40 text-sm text-center py-4">
                  Aucune épaisseur configurée. Cliquez sur "Ajouter épaisseur" pour commencer.
                </p>
              ) : (
                <div className="space-y-3">
                  {formData.thickness_options.map((opt, index) => (
                    <div key={index} className="p-3 bg-[#0A0A0A] border border-white/10 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={opt.available}
                            onCheckedChange={(checked) => updateThicknessOption(index, 'available', checked)}
                            className="border-white/20 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                          />
                          <span className="text-white/60 text-sm">Disponible</span>
                        </div>
                        <button 
                          type="button" 
                          onClick={() => removeThicknessOption(index)}
                          className="p-1 text-red-400 hover:text-red-300"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-white/40 text-xs mb-1 block">Épaisseur</label>
                          <Select 
                            value={opt.thickness} 
                            onValueChange={(v) => updateThicknessOption(index, 'thickness', v)}
                          >
                            <SelectTrigger className="bg-[#1C1C1C] border-white/10 text-white rounded-none text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-[#1C1C1C] border-white/10">
                              {availableThicknesses.map((t) => (
                                <SelectItem key={t} value={t} className="text-white hover:bg-white/10">{t}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-white/40 text-xs mb-1 block">Prix / m²</label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={opt.price_per_m2}
                            onChange={(e) => updateThicknessOption(index, 'price_per_m2', e.target.value)}
                            className="bg-[#1C1C1C] border-white/10 text-white rounded-none text-sm"
                            placeholder="0"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-4 border-t border-white/10">
              <button type="button" onClick={() => setIsDialogOpen(false)} className="btn-secondary flex-1">
                Annuler
              </button>
              <button type="submit" className="btn-primary flex-1 flex items-center justify-center gap-2">
                <Save className="w-4 h-4" />
                Enregistrer
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Nouvelle Catégorie */}
      <Dialog open={isNewCategoryDialogOpen} onOpenChange={setIsNewCategoryDialogOpen}>
        <DialogContent className="bg-[#1C1C1C] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Nouvelle catégorie</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-white/60 text-sm mb-2 block">Nom de la catégorie *</label>
              <Input
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="Ex: Calacatta, Marbre, etc."
                className="bg-[#0A0A0A] border-white/10 text-white rounded-none"
                data-testid="new-category-name-input"
              />
            </div>
            <div className="flex items-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => { setIsNewCategoryDialogOpen(false); setNewCategoryName(''); }}
                className="px-4 py-2 border border-white/20 text-white/70 hover:text-white text-sm"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleCreateCategory}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
                data-testid="confirm-new-category-btn"
              >
                <Save className="w-4 h-4" />
                Créer
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
